import React, { useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { Helmet } from '@vuer-ai/react-helmet-async';
import { useModeTokens } from '@/common/components/ui';
import { GoogleSignInProvider } from '@/common/auth/components/GoogleSignIn';
import { LandingHeader } from './LandingHeader';
import { LandingHero } from './LandingHero';
import { LandingFeatures } from './LandingFeatures';
import { LandingHowItWorks } from './LandingHowItWorks';
import { LandingCollaboration } from './LandingCollaboration';
import { LandingCta } from './LandingCta';
import { LandingFooter } from './LandingFooter';
import { DownloadDialog } from './DownloadDialog';
import { CONTENT_MAX_WIDTH } from './primitives';
import { LandingAtmosphere, LandingReveal } from './LandingEffects';
import { LandingExplore } from './LandingExplore';
import { LandingFaq } from './LandingFaq';
import { LandingHandoff } from './LandingHandoff';
import './landing.css';
import './gloss.css';

/**
 * Marketing and sign-in for browser visitors. Desktop has its own login card.
 *
 * Sign-in is provided once at the top so the header link, the hero CTA and the
 * closing CTA all trigger the same single live action.
 */
export const LandingPage: React.FC = () => {
  const t = useModeTokens();
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [motionPaused, setMotionPaused] = useState(false);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const motionEnabled = !reducedMotion && !motionPaused;

  return (
    <GoogleSignInProvider>
      <Helmet>
        <title>Studio Vault — version control for music sessions</title>
        <meta
          name="description"
          content="Finish the music. Keep the history. Version your music projects, revisit older sessions, share stems and organize releases with StudioVault."
        />
      </Helmet>
      {/* The painted canvas stays full-bleed; only the content is capped. */}
      <Box
        className={`lp-page${motionEnabled ? '' : ' lp-motion-off'}`}
        sx={{
          minHeight: '100vh',
          width: '100%',
          overflowX: 'hidden',
          backgroundColor: t.surfaces.canvas,
          color: t.text.primary,
          '--lp-glow': t.colors.accentFill,
          '--lp-glass-base': t.surfaces.panel,
          '--lp-glass-screen': t.surfaces.screen,
        }}
      >
        {/* The design composes at a fixed 1420px. Past that the hero grid and
            the proof card just get bigger, which reads as a stretched
            screenshot rather than a wider page. */}
        <Box sx={{ maxWidth: CONTENT_MAX_WIDTH, marginX: 'auto' }}>
          <LandingHeader onGetTheApp={() => setDownloadOpen(true)} />
          <Box sx={{ position: 'relative', isolation: 'isolate' }}>
            <LandingAtmosphere enabled={motionEnabled} />
            <LandingHero onDownload={() => setDownloadOpen(true)} />
            {!reducedMotion && (
              <Box sx={{ px: { xs: '20px', md: '40px' }, textAlign: 'right' }}>
                <Box
                  component="button"
                  onClick={() => setMotionPaused((paused) => !paused)}
                  aria-pressed={motionPaused}
                  sx={{
                    font: 'inherit',
                    fontSize: 11,
                    color: t.text.secondary,
                    background: 'none',
                    border: '1px solid',
                    borderColor: t.borders.subtle,
                    borderRadius: '999px',
                    p: '6px 12px',
                    cursor: 'pointer',
                  }}
                >
                  {motionPaused
                    ? 'Resume ambient motion'
                    : 'Pause ambient motion'}
                </Box>
              </Box>
            )}
          </Box>
          <LandingReveal>
            <LandingHandoff />
          </LandingReveal>
          <LandingReveal>
            <LandingFeatures />
          </LandingReveal>
          <LandingReveal>
            <LandingExplore />
          </LandingReveal>
          <LandingReveal>
            <LandingHowItWorks />
          </LandingReveal>
          <LandingReveal>
            <LandingCollaboration />
          </LandingReveal>
          <LandingReveal>
            <LandingFaq onDownload={() => setDownloadOpen(true)} />
          </LandingReveal>
          <LandingReveal>
            <LandingCta onDownload={() => setDownloadOpen(true)} />
          </LandingReveal>
          <LandingFooter onDownload={() => setDownloadOpen(true)} />
        </Box>
      </Box>
      <DownloadDialog
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
      />
    </GoogleSignInProvider>
  );
};

export default LandingPage;
